/**
 * Generato automaticamente.
 * framework-cfg-utenti
 */
import { useMutation } from 'react-query';
import type { MutationFunction, UseMutationOptions } from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type { AuditDTO } from '.././schemas';

// https://stackoverflow.com/questions/49579094/typescript-conditional-types-filter-out-readonly-properties-pick-only-requir/49579497#49579497
type IfEquals<X, Y, A = X, B = never> = (<T>() => T extends X ? 1 : 2) extends <
  T,
>() => T extends Y ? 1 : 2
  ? A
  : B;

type WritableKeys<T> = {
  [P in keyof T]-?: IfEquals<
    { [Q in P]: T[P] },
    { -readonly [Q in P]: T[P] },
    P
  >;
}[keyof T];

type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (
  k: infer I,
) => void
  ? I
  : never;
type DistributeReadOnlyOverUnions<T> = T extends any ? NonReadonly<T> : never;

type Writable<T> = Pick<T, WritableKeys<T>>;
type NonReadonly<T> = [T] extends [UnionToIntersection<T>]
  ? {
      [P in keyof Writable<T>]: T[P] extends object
        ? NonReadonly<NonNullable<T[P]>>
        : T[P];
    }
  : DistributeReadOnlyOverUnions<T>;

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Inserisce un audit per un servizio interno
 * @summary Inserisci audit servizio interno
 */
export const postAudit = (
  auditDTO: NonReadonly<AuditDTO>,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-cfg-utenti/audit`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: auditDTO,
    },
    options,
  );
};

export const getPostAuditMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postAudit>>,
    TError,
    { data: NonReadonly<AuditDTO> },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postAudit>>,
  TError,
  { data: NonReadonly<AuditDTO> },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postAudit>>,
    { data: NonReadonly<AuditDTO> }
  > = props => {
    const { data } = props ?? {};

    return postAudit(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostAuditMutationResult = NonNullable<
  Awaited<ReturnType<typeof postAudit>>
>;
export type PostAuditMutationBody = NonReadonly<AuditDTO>;
export type PostAuditMutationError = ErrorType<unknown>;

/**
 * @summary Inserisci audit servizio interno
 */
export const usePostAudit = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postAudit>>,
    TError,
    { data: NonReadonly<AuditDTO> },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostAuditMutationOptions(options);

  return useMutation(mutationOptions);
};
